import axios from "axios";
// Assuming AxiosPrivate and VerifyLoggedIn are defined in a file called "./Axios"
import { AxiosPrivate, VerifyLoggedIn } from "./Axios";

const development = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
const backendUrl = development ? 'http://127.0.0.1:8000/bookings/' : 'https://api.yeticlean.co.uk/bookings/';

// --- Helper Functions (Unchanged) ---
function ensureArray(data) {
    return Array.isArray(data) ? data : [];
}

function ensureObject(data) {
    if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
        return data;
    }
    return null;
}

function toSnakeCase(obj) {
    if (typeof obj !== 'object' || obj === null) return obj;
    const newObj = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
            newObj[snakeKey] = obj[key];
        }
    }
    return newObj;
}


// --- Base Axios Instance (MODIFIED) ---
export const AxiosPublic = axios.create({
    baseURL: backendUrl,
    // --- FIX: The default "Content-Type" header has been removed. ---
    // Axios will now automatically set the correct header for both JSON and FormData.
    withCredentials: true,
});

AxiosPublic.interceptors.response.use(
    (initialResponse) => initialResponse,
    async (error) => {
        if (error.response?.status === 401) {
            try {
                const response = await VerifyLoggedIn();
                if (response.status === 200) {
                    return axios.request(error.config);
                }
            } catch (err) {
                // Intentionally fail silently
            }
        }
        return Promise.reject(error);
    }
);


class CrmApiService {
    constructor(axiosInstance) {
        this.axios = axiosInstance;
    }

    _getCurrentLocationSlug() {
        const storedLocation = localStorage.getItem('location');
        if (!storedLocation) {
            throw new Error("API Service Error: Location has not been set. Call ApiService.setLocation() first.");
        }
        return storedLocation.toLowerCase().replace(/ /g, '_');
    }

    setLocation(locationName) {
        if (!locationName) {
            localStorage.removeItem('location');
            return;
        }
        localStorage.setItem('location', locationName);
    }

    getFranchiseUrl() {
        const locationSlug = this._getCurrentLocationSlug();
        return `crm/franchises/${locationSlug}`;
    }

    getStatistics(locations, startDate, endDate, signal) {
        const locationSlug = locations.map(loc => loc.toLowerCase().replace(/ /g, '_')).join(',');
        const params = {
            startDate: startDate,
            start_date: startDate,
            endDate: endDate,
            end_date: endDate
        };
        return this.axios.get(`crm/franchises/${locationSlug}/statistics/`, { params, signal }).then(res => res.data);
    }

    getAdminStatistics() {
        return this.axios.get(`crm/franchises/statistics/`).then(res => res.data);
    }

    getCampaignStatistics(days = 30) {
        return this.axios.get(`crm/campaigns/statistics/?days=${days}`).then(res => res.data);
    }

    getCampaignLeakage(startDate, endDate) {
        return this.axios.get(`crm/campaigns/leakage/`, {
            params: {
                start_date: startDate,
                end_date: endDate
            }
        }).then(res => res.data);
    }

    getReturnCalculatorData(franchiseId, platform) {
        let url = `crm/campaigns/return-calculator/?`;
        if (franchiseId && franchiseId !== 'none') url += `franchise_id=${franchiseId}&`;
        if (platform && platform !== 'none') url += `platform=${platform}&`;
        return this.axios.get(url).then(res => res.data);
    }

    getCampaignCalculatorFranchises() {
        return this.axios.get(`crm/campaigns/calculator-data/`).then(res => res.data);
    }

    getCampaignCalculatorData(franchiseId) {
        return this.axios.get(`crm/campaigns/calculator-data/?franchise_id=${franchiseId}`).then(res => res.data);
    }

    /** Ad Controls tab: list all franchises with ad platform toggle fields (uses root API, not /bookings/). */
    getAdPlatformToggles() {
        return AxiosPrivate.get('franchise/ad-platform-toggles/').then(res => res.data);
    }

    /** Ad Controls tab: update one franchise's Facebook or Google ad platform toggle. */
    setAdPlatformToggle(franchiseId, field, value) {
        const payload = { franchise_id: franchiseId, [field]: value };
        return AxiosPrivate.post('franchise/ad-platform-toggles/', payload).then(res => res.data);
    }

    getBookings(options) {
        let params = { ...options };
        if (options.bySms) {
            params['filter_phone_numbers[]'] = options.bySms;
            delete params.bySms;
        }
        const snakeCaseParams = toSnakeCase(params);
        return this.axios.get(`${this.getFranchiseUrl()}/bookings/`, { params: snakeCaseParams }).then(res => res.data);
    }

    getLeafletRequests(options) {
        const params = toSnakeCase(options || {});
        return this.axios.get(`${this.getFranchiseUrl()}/leaflet-requests/`, { params }).then(res => res.data);
    }

    createLeafletRequest(data) {
        return this.axios.post(`${this.getFranchiseUrl()}/leaflet-requests/`, data).then(res => res.data);
    }

    getBookingsToday(date) {
        return this.axios.get(`${this.getFranchiseUrl()}/bookings-today/`, { params: { date } }).then(res => ensureArray(res.data.results));
    }

    getLeads(options) {
        let params = { ...options };
        if (options.bySms) {
            params['filter_phone_numbers[]'] = options.bySms;
            delete params.bySms;
        }
        const snakeCaseParams = toSnakeCase(params);
        return this.axios.get(`${this.getFranchiseUrl()}/leads/`, { params: snakeCaseParams }).then(res => res.data);
    }

    assignLeadToSelf(id) {
        return this.axios.post(`crm/leads/${id}/assign-self/`).then(res => res.data);
    }

    unassignLeadFromSelf(id) {
        return this.axios.delete(`crm/leads/${id}/assign-self/`).then(res => res.data);
    }

    getCustomers(options) {
        return this.axios.get(`${this.getFranchiseUrl()}/customers/`, { params: options }).then(res => res.data);
    }

    getRecurringBookings(options) {
        return this.axios.get(`${this.getFranchiseUrl()}/recurring-bookings/`, { params: options }).then(res => res.data);
    }

    getAllBookingImages() {
        return this.axios.get(`${this.getFranchiseUrl()}/booking-images/`).then(res => ensureArray(res.data.results));
    }

    getAllFranchiseSalesTeamStats() {
        return this.axios.get(`${this.getFranchiseUrl()}/sales-team/`).then(res => res.data);
    }

    getUnconfirmedCustomerRoofs() {
        return this.axios.get(`${this.getFranchiseUrl()}/unconfirmed-roofs/`).then(res => ensureArray(res.data));
    }

    getSalesStats() { return this.axios.get(`crm/stats/`).then(res => res.data); }
    getBookingById(id) { return this.axios.get(`crm/bookings/${id}/`).then(res => res.data); }
    getBookingReturns(bookingId) { return this.axios.get(`crm/bookings/${bookingId}/returns/`).then(res => ensureArray(res.data)); }
    createBookingReturn(bookingId, data) { return this.axios.post(`crm/bookings/${bookingId}/returns/`, data).then(res => res.data); }
    updateBookingReturn(returnId, data) { return this.axios.put(`crm/bookings/returns/${returnId}/`, data).then(res => res.data); }
    deleteBookingReturn(returnId) { return this.axios.delete(`crm/bookings/returns/${returnId}/`).then(res => res.data); }
    deleteBookingById(id) { return this.axios.delete(`crm/bookings/${id}/`).then(res => res.data); }
    updateBooking(id, data, notify = true) { return this.axios.put(`crm/bookings/${id}/`, { ...data, notify }).then(res => res.data); }
    createBooking(data) { return this.axios.post(`crm/franchises/${this._getCurrentLocationSlug()}/bookings/`, data).then(res => res.data); }
    updateBookingTime(id, date, notify = true) { return this.axios.put(`crm/bookings/${id}/update-date/`, { date, notify }).then(res => res.data); }
    rescheduleBooking(id, date) { return this.axios.put(`crm/bookings/${id}/reschedule/`, { date }).then(res => res.data); }
    cancelBooking(id) { return this.axios.put(`crm/bookings/${id}/cancel/`).then(res => res.data); }
    runCompletedBookingAction(id, action) { return this.axios.post(`crm/bookings/${id}/completed-actions/${action}/`).then(res => res.data); }
    getNeedsReschedulingBookings(options) {
        const snakeCaseParams = toSnakeCase(options);
        return this.axios.get(`${this.getFranchiseUrl()}/needs-rescheduling/`, { params: snakeCaseParams }).then(res => res.data);
    }
    getNeedsReschedulingCount() {
        return this.axios.get(`${this.getFranchiseUrl()}/needs-rescheduling/count/`).then(res => res.data);
    }

    getLeadById(id) { return this.axios.get(`crm/leads/${id}/`).then(res => res.data); }
    deleteLeadById(id) { return this.axios.delete(`crm/leads/${id}/`).then(res => res.data); }
    updateLead(id, data) { return this.axios.put(`crm/leads/${id}/`, data).then(res => res.data); }
    updateLeadPreBookingAnswers(id, data) { return this.axios.post(`crm/leads/${id}/pre-booking-answers/`, data).then(res => res.data); }
    createLead(data) { return this.axios.post(`crm/franchises/${this._getCurrentLocationSlug()}/leads/`, data).then(res => res.data); }

    getCustomerById(id) { return this.axios.get(`crm/customers/${id}/`).then(res => res.data); }
    deleteCustomerById(id) { return this.axios.delete(`crm/customers/${id}/`).then(res => res.data); }
    updateCustomer(id, data) { return this.axios.put(`crm/customers/${id}/`, data).then(res => res.data); }

    getRecurringBookingById(id) { return this.axios.get(`crm/recurring-bookings/${id}/`).then(res => res.data); }
    updateRecurringBooking(id, data) { return this.axios.put(`crm/recurring-bookings/${id}/`, data).then(res => res.data); }
    createRecurringBooking(data) { return this.axios.post(`crm/franchises/${this._getCurrentLocationSlug()}/recurring-bookings/`, data).then(res => res.data); }

    getRecurringBookingByBookingId(bookingId) { return this.axios.get(`crm/recurring-bookings/from-booking/${bookingId}/`).then(res => ensureArray(res.data)); }

    employeeUpdateBooking(params) {
        return this.axios.post(`crm/bookings/employee-update/`, null, { params }).then(res => res.data);
    }

    sendKlarnaInvoice(booking_id) {
        return this.axios.post(`crm/bookings/send-klarna-invoice/`, null, { params: { booking_id } }).then(res => res.data);
    }

    getLatestLeadByCustomerId(customerId) { return this.axios.get(`crm/customers/${customerId}/latest-lead/`).then(res => res.data); }

    getCustomerLatestSentiment(customerId) { 
        return this.axios.get(`crm/customers/${customerId}/latest-sentiment/`).then(res => res.data); 
    }

    getActionLogByBookingID(bookingId) { return this.axios.get(`crm/action-logs/booking/${bookingId}/`).then(res => ensureArray(res.data)); }
    getActionLogByLeadID(leadId) { return this.axios.get(`crm/action-logs/lead/${leadId}/`).then(res => ensureArray(res.data)); }

    getBookingImages(bookingId) { return this.axios.get(`crm/booking-images/booking/${bookingId}/`).then(res => ensureArray(res.data)); }

    // --- NO CHANGES NEEDED HERE ---
    // Because the global fix was applied, this code is now correct as-is.
    postBookingImages(bookingId, formData) {
        return this.axios.post(
            `crm/booking-images/booking/${bookingId}/`,
            formData
        ).then(res => ensureArray(res.data));
    }

    // --- NO CHANGES NEEDED HERE ---
    updateBookingImageFile(imageId, formData) {
        return this.axios.put(
            `crm/booking-images/${imageId}/`,
            formData
        ).then(res => ensureObject(res.data));
    }

    deleteBookingImage(imageId) { return this.axios.delete(`crm/booking-images/${imageId}/`); }

    // --- NO CHANGES NEEDED HERE ---
    updateCustomerRoofImage(roofId, imageFile) {
        const formData = new FormData();
        formData.append('roof_uploaded_image', imageFile);
        return this.axios.post(`crm/customer-roofs/${roofId}/update-image/`, formData).then(res => res.data);
    }

    confirmCustomerRoofPin(roofId, data) {
        return this.axios.post(`crm/customer-roofs/${roofId}/confirm-pin/`, data).then(res => res.data);
    }

    confirmCustomerRoof(roofId, sqm, price) {
        return this.axios.post(`crm/customer-roofs/${roofId}/confirm/${sqm}/${price}/`).then(res => res.data);
    }

    downloadCustomerRoofQuote(roofId, sqm, price) {
        return this.axios.post(`crm/customer-roofs/download/${roofId}/${sqm}/${price}/`, {}, { responseType: 'blob' });
    }

    calculateRoofPricePreview(data) {
        return this.axios.post('crm/customer-roofs/price-preview/', data).then(res => res.data);
    }

    // Unassigned Leads Management (Super Admin)
    getUnassignedLeads() {
        return this.axios.get('crm/unassigned-leads/').then(res => res.data);
    }

    assignFranchiseByCustomer(customerId, franchiseId) {
        return this.axios.post('crm/assign-franchise-by-customer/', {
            customer_id: customerId,
            franchise_id: franchiseId
        }).then(res => res.data);
    }

    // Territory Map (Super Admin)
    getTerritoryFranchises() {
        return AxiosPrivate.get('franchise/territory/franchises/').then(res => res.data);
    }

    getTerritoryMapping() {
        return AxiosPrivate.get('franchise/territory/mapping/').then(res => res.data);
    }

    updateTerritoryBatch(franchiseId, add, remove) {
        return AxiosPrivate.post('franchise/territory/batch-update/', {
            franchise_id: franchiseId,
            add,
            remove,
        }).then(res => res.data);
    }

    updateTerritoryFranchiseLocation(franchiseId, latitude, longitude) {
        return AxiosPrivate.post('franchise/territory/franchise-location/', {
            franchise_id: franchiseId,
            latitude,
            longitude,
        }).then(res => res.data);
    }

    // New Pricing Structure Stats (Super Admin)
    getNewPricingStats() {
        return this.axios.get('crm/new-pricing-stats/').then(res => res.data);
    }

    getAffluencePricingStats() {
        return this.axios.get('crm/affluence-pricing-stats/').then(res => res.data);
    }

    getSalesPerformanceTracker(salespersonId, startDate, endDate, signal) {
        const params = {
            startDate,
            start_date: startDate,
            endDate,
            end_date: endDate,
        };
        if (salespersonId) {
            params.salesperson_id = salespersonId;
        }
        return this.axios.get('crm/sales-performance-tracker/', { params, signal }).then(res => res.data);
    }
}

const ApiService = new CrmApiService(AxiosPublic);
export default ApiService;

// --- Functions below here are unchanged ---

export function getServicePrices(tier, house_type) {
    return AxiosPublic.get('service-prices/', { params: { tier, house_type } }).then(res => res.data);
}

export function getBookingExemptDates(locationName) {
    const locationSlug = locationName.toLowerCase().replace(/ /g, '_');
    return AxiosPublic.get(`get-exempt-booking-dates/${locationSlug}/`).then(res => res.data);
}

export function prepareBookingData(bookingData, services, recurringBookings) {
    const data = { ...bookingData };
    data.services = [];
    data.service_price = {};
    if (recurringBookings?.length > 0) {
        data.recurring_booking = recurringBookings;
    }
    Object.values(services).forEach((service) => {
        if (service.selected) {
            data.services.push(service.name);
            if (service.price !== 0) {
                data.service_price[service.name] = service.price;
            }
        }
    });
    return data;
}

export function prepareLeadData(leadData, services) {
    const data = { ...leadData };
    data.services = [];
    data.service_price = {};
    Object.values(services).forEach((service) => {
        if (service.selected) {
            data.services.push(service.name);
            if (service.price !== 0) {
                data.service_price[service.name] = service.price;
            }
        }
    });
    return data;
}